import { normalizeReferralCode } from "../../../api/waitlistStanding";
import { SITE_ORIGIN } from "../../../config/site";

/** `?ref=CODE` from the visit, kept for the session so it survives navigation. */
export const INCOMING_REF_STORAGE_KEY = "qi_waitlist_ref";
/** This browser's own referral code, saved after joining so a return visit can show the place in line. */
export const OWN_REFERRAL_CODE_STORAGE_KEY = "qi_waitlist_my_code";

export const REFERRAL_SHARE_ORIGIN = SITE_ORIGIN;
/** Places a person moves up for each friend who joins with their link (contract, 2026-10-27). */
export const PLACES_PER_REFERRAL = 5;

function safeGet(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: () => Storage, key: string, value: string): void {
  try {
    storage().setItem(key, value);
  } catch {
    // Private mode / blocked storage: the ref still rides along on this page load.
  }
}

function safeRemove(storage: () => Storage, key: string): void {
  try {
    storage().removeItem(key);
  } catch {
    // ignore
  }
}

const session = () => window.sessionStorage;
const local = () => window.localStorage;

export function parseRefFromSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return normalizeReferralCode(params.get("ref"));
}

/** Stores a valid `?ref=` from the URL (latest link wins) and returns the code in effect. */
export function persistRefFromSearch(search: string): string | null {
  const fromUrl = parseRefFromSearch(search);
  if (fromUrl) {
    safeSet(session, INCOMING_REF_STORAGE_KEY, fromUrl);
    return fromUrl;
  }
  return getStoredRef();
}

export function getStoredRef(): string | null {
  return normalizeReferralCode(safeGet(session, INCOMING_REF_STORAGE_KEY));
}

export function readOwnReferralCode(): string | null {
  return normalizeReferralCode(safeGet(local, OWN_REFERRAL_CODE_STORAGE_KEY));
}

export function saveOwnReferralCode(code: string): void {
  safeSet(local, OWN_REFERRAL_CODE_STORAGE_KEY, code);
}

export function clearOwnReferralCode(): void {
  safeRemove(local, OWN_REFERRAL_CODE_STORAGE_KEY);
}

export function referralShareUrl(code: string): string {
  return `${REFERRAL_SHARE_ORIGIN}/?ref=${encodeURIComponent(code)}`;
}

export const REFERRAL_SHARE_TEXT =
  "I joined the QInvesting waitlist. An AI builds a portfolio from your money and how big a drop you can live with. Join with my link:";

export type ShareChannel = "native" | "x" | "linkedin" | "whatsapp" | "email";

export type ShareLink = { channel: Exclude<ShareChannel, "native">; label: string; href: string };

export function referralShareLinks(code: string): ShareLink[] {
  const url = referralShareUrl(code);
  const text = REFERRAL_SHARE_TEXT;
  const enc = encodeURIComponent;
  return [
    {
      channel: "x",
      label: "X",
      href: `https://x.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`,
    },
    {
      channel: "linkedin",
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    },
    {
      channel: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${enc(`${text} ${url}`)}`,
    },
    {
      channel: "email",
      label: "Email",
      href: `mailto:?subject=${enc("Join me on the QInvesting waitlist")}&body=${enc(`${text}\n\n${url}`)}`,
    },
  ];
}
